/** @jsxImportSource @emotion/react */
import { ProofTypes } from '@notabene/javascript-sdk';
import { IconName, ListItem, Logo, useTheme } from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getOriginatorInfo,
  useAddCollectedOriginatorData,
  useGetOriginatorInfo,
} from './api';
import { ProofOfOwnership } from './ProofOfOwnership';
import { useTravelRuleOriginatorWidget } from './useTravelRuleOriginatorWidget';
import { exceedsProofOfOwnershipThreshold } from './utils';

export const TravelRuleOriginatorInfo = () => {
  const { transactionId } = useParams();
  const { data: originatorInfo } = useGetOriginatorInfo(transactionId);
  const {
    mutate: addCollectedOriginatorData,
    isPending: isAddingCollectedOriginatorData,
    isSuccess: isAddingCollectedOriginatorDataSuccess,
    isError: isAddingCollectedOriginatorDataError,
  } = useAddCollectedOriginatorData();
  const { spacing } = useTheme();
  const queryParams = new URLSearchParams(window.location.search);
  const showProofOfOwnership =
    queryParams.get('showProofOfOwnership') === 'true';
  const brand = queryParams.get('brand');
  const [showSupplyProofOfOwnership, setShowSupplyProofOfOwnership] =
    useState(showProofOfOwnership);
  const [isSelfDeclaration, setIsSelfDeclaration] = useState(false);

  useEffect(() => {
    if (isSelfDeclaration) {
      const exceedsThreshold = originatorInfo?.euroAmount
        ? exceedsProofOfOwnershipThreshold(originatorInfo.euroAmount)
        : false;

      if (exceedsThreshold) {
        setShowSupplyProofOfOwnership(true);
      }
    }
  }, [isSelfDeclaration, originatorInfo?.euroAmount]);

  useTravelRuleOriginatorWidget({
    init: async () => {
      if (!transactionId) {
        throw new Error('Missing transactionId');
      }
      const initData = await getOriginatorInfo(transactionId);
      return initData;
    },
    onCompletion: (data) => {
      if (!transactionId) {
        throw new Error('Missing transactionId');
      }
      addCollectedOriginatorData({ transactionId, data });
      setIsSelfDeclaration(
        data.txUpdate?.originatorProof?.type === ProofTypes.SelfDeclaration
      );
    },
    mountOnElementId: 'nb-deposit',
    brand,
  });

  if (showSupplyProofOfOwnership) {
    return <ProofOfOwnership transactionId={transactionId} />;
  }

  return (
    <div
      css={{
        padding: spacing.xs,
      }}
    >
      {!brand && (
        <div
          css={{
            display: 'grid',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: spacing.xs,
          }}
        >
          <Logo width={spacing.xxxl} />
        </div>
      )}
      <div id="nb-deposit" css={{ maxHeight: '85vh', overflow: 'scroll' }} />
      {(isAddingCollectedOriginatorData ||
        isAddingCollectedOriginatorDataSuccess) && (
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            gap: spacing.xs,
            alignItems: 'center',
            marginTop: spacing.xs,
          }}
        >
          <ListItem
            icon={'spinner' as IconName}
            subtitle={
              isAddingCollectedOriginatorData
                ? 'Submitting information...'
                : 'Information submitted, you can now close this window.'
            }
          />
        </div>
      )}
      {isAddingCollectedOriginatorDataError && (
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            gap: spacing.xs,
            alignItems: 'center',
            marginTop: spacing.xs,
          }}
        >
          <ListItem
            icon={'error' as IconName}
            subtitle="Failed to submit information. Please refresh the page and try again."
          />
        </div>
      )}
    </div>
  );
};

export default TravelRuleOriginatorInfo;
